import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import SEOHead from '../components/SEOHead';
import BrandLoadingScreen from '../components/BrandLoadingScreen';

function VerificationBadge({ house }) {
  const status = house.status;
  const isVerified = house.is_verified;

  if (status === 'verified' || isVerified === true) {
    return (
      <span className="bg-emerald-100 text-emerald-700 px-5 py-2 rounded-full font-medium flex items-center gap-2">
        <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
        ✓ Verified Property
      </span>
    );
  }

  if (status === 'sample') {
    return (
      <span className="bg-violet-100 text-violet-700 px-5 py-2 rounded-full font-medium flex items-center gap-2">
        <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
        Sample Listing
      </span>
    );
  }

  return (
    <span className="bg-amber-100 text-amber-700 px-5 py-2 rounded-full font-medium flex items-center gap-2">
      <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
      </svg>
      Pending Verification
    </span>
  );
}

export default function HouseDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [house, setHouse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentImage, setCurrentImage] = useState(0);
  const [isSaved, setIsSaved] = useState(false);

  const adminToken = localStorage.getItem('adminAuthToken');
  const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
  const isAdminLoggedIn = !!adminToken && adminUser.role;

  useEffect(() => {
    const fetchHouse = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/houses/${id}`);
        if (res.data.status === 'success') {
          setHouse(res.data.house);
          // ✅ Start gallery on the cover image set by admin
          setCurrentImage(res.data.house.cover_image_index ?? 0);
        } else {
          setError(res.data.message || 'Failed to load house details');
        }
      } catch (err) {
        console.error('House fetch error:', err);
        setError(err.response?.data?.message || 'Failed to load house details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    fetchHouse();
  }, [id]);

  useEffect(() => {
    if (house && user) {
      const savedHouses = JSON.parse(localStorage.getItem('savedHouses') || '[]');
      setIsSaved(savedHouses.includes(house.id));
    }
  }, [house, user]);

  const handleJoinWaitlist = () => navigate('/waitlist', { state: { house } });
  const handleApplyNow = () => navigate('/apply', { state: { house } });

  const handleSaveToggle = () => {
    if (!user) { alert('Please login to save favorites'); return; }
    const savedHouses = JSON.parse(localStorage.getItem('savedHouses') || '[]');
    if (isSaved) {
      localStorage.setItem('savedHouses', JSON.stringify(savedHouses.filter(hid => hid !== house.id)));
      setIsSaved(false);
      alert('Removed from favorites!');
    } else {
      savedHouses.push(house.id);
      localStorage.setItem('savedHouses', JSON.stringify(savedHouses));
      setIsSaved(true);
      alert('Added to favorites!');
    }
  };

  if (loading) {
    return <BrandLoadingScreen message="Loading this home for you…" />;
  }

  if (error || !house) {
    return (
      <div className="min-h-screen bg-[#fdfdfd] flex flex-col items-center justify-center px-6 py-12">
        <div className="text-red-600 text-2xl font-bold mb-6">Error</div>
        <p className="text-[#1a1a1a] text-xl mb-8 text-center max-w-md">{error || 'House not found'}</p>
        <Link to="/browse" className="px-10 py-4 bg-[#294c79] text-white rounded-full font-medium hover:bg-[#1f3a5c] transition shadow-md">
          Back to Browse
        </Link>
      </div>
    );
  }

  const images = house.images || [];
  const annualRent = house.annual_rent || 0;
  const isSampleListing = house.status === 'sample';
  const isApplicationReady = ['pending', 'verified'].includes(String(house.status || '').toLowerCase()) || house.is_verified === true;

  return (
    <>
      <SEOHead
        title={`${house.title || 'House'} | LiteRent Ibadan`}
        description={`See ${house.title || 'this home'} in ${house.location || 'Ibadan'} and explore LiteRent's 30% upfront rent plan.`}
        canonical={`https://literent.com.ng/house/${id}`}
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'Residence',
          name: house.title || 'LiteRent Home',
          description: house.description || `Verified home in ${house.location || 'Ibadan'} available on LiteRent`,
          address: { '@type': 'PostalAddress', addressLocality: house.location || 'Ibadan', streetAddress: house.address || '' },
          image: house.images?.[house.cover_image_index ?? 0] || house.images?.[0] || 'https://literent.com.ng/Logo.jpg',
          offers: { '@type': 'Offer', priceCurrency: 'NGN', price: house.annual_rent || 0, availability: house.is_available ? 'https://schema.org/InStock' : 'https://schema.org/SoldOut' }
        }}
      />
    <div className="min-h-screen bg-[#fdfdfd] py-12 px-6">
      <div className="max-w-7xl mx-auto">
        <Link to="/browse" className="text-[#294c79] hover:underline mb-8 inline-block text-lg font-medium">
          ← Back to Browse Page
        </Link>

        {/* Sample listing notice */}
        {house.status === 'sample' && (
          <div className="mb-6 bg-amber-50 border border-amber-200 rounded-2xl px-6 py-4 text-sm text-amber-800">
            This is a preview sample listing and is not currently available for rent.
            <Link to="/waitlist" className="ml-1 font-semibold underline hover:text-amber-900">
              Join the waitlist
            </Link> to be first in line when we go live!
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-12">

          {/* Image Gallery */}
          <div>
            <div className="relative h-[500px] md:h-[600px] rounded-2xl overflow-hidden shadow-2xl bg-gray-100 mb-6">
              {images.length > 0 ? (
                <img
                  src={images[currentImage]}
                  alt={`${house.title} view ${currentImage + 1}`}
                  className="w-full h-full object-cover transition-transform duration-500"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400 text-2xl font-medium">
                  No images available
                </div>
              )}
            </div>
            {images.length > 1 && (
              <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-3 overflow-x-auto pb-4">
                {images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentImage(idx)}
                    className={`rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                      currentImage === idx
                        ? 'border-[#a5c250] ring-2 ring-[#a5c250]/50 scale-105'
                        : 'border-transparent hover:border-[#a5c250]/70'
                    }`}
                  >
                    <img src={img} alt={`Thumbnail ${idx + 1}`} className="w-full h-20 object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* House Details */}
          <div>
            <h1 className="text-4xl md:text-5xl font-bold text-[#294c79] mb-6">
              {house.title || 'Property Details'}
            </h1>

            <div className="flex flex-wrap gap-4 mb-8">
              <VerificationBadge house={house} />
              <span className="bg-gray-100 px-5 py-2 rounded-full text-lg">
                {house.bedrooms} Bedroom{house.bedrooms !== 1 ? 's' : ''}
              </span>
              <span className="bg-gray-100 px-5 py-2 rounded-full text-lg">
                {house.bathrooms} Bathroom{house.bathrooms !== 1 ? 's' : ''}
              </span>
              <span className="bg-gray-100 px-5 py-2 rounded-full text-lg">
                📍 {house.location || 'Location not specified'}
              </span>
            </div>

            <div className="text-4xl font-bold text-[#294c79] mb-4">
              ₦{annualRent.toLocaleString()}
              <span className="text-2xl font-normal text-[#1a1a1a]/70"> / year</span>
            </div>

            {house.description && house.description.trim() ? (
              <p className="text-xl text-[#1a1a1a]/80 mb-10 leading-relaxed">
                {house.description}
              </p>
            ) : null}

            <div className="bg-[#e8f2d1]/30 p-8 rounded-2xl mb-10">
              <h3 className="text-2xl font-semibold text-[#294c79] mb-6 text-center">LiteRent Payment Plan</h3>
              <div className="grid md:grid-cols-3 gap-6 text-center">
                <div>
                  <p className="text-sm text-[#1a1a1a]/70">Upfront (30%)</p>
                  <p className="text-2xl font-bold text-[#294c79]">₦{Math.ceil(annualRent * 0.3).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-[#1a1a1a]/70">Verification Fee</p>
                  <p className="text-2xl font-bold text-[#294c79]">₦{(annualRent >= 1000000 ? 20000 : 10000).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-[#1a1a1a]/70">Monthly (approx.)</p>
                  <p className="text-3xl font-bold text-[#a5c250]">
                    ₦{Math.ceil((annualRent * 0.7 + (annualRent * 0.7 * 0.048 * 11)) / 11).toLocaleString()}
                  </p>
                </div>
              </div>
              <p className="text-sm text-center text-[#1a1a1a]/70 mt-6">
                Includes 4.8% monthly interest on remaining balance (11 months)
              </p>
            </div>

            {!isAdminLoggedIn && (
              <div className="text-center py-10">
                <p className="text-3xl font-medium text-[#294c79] mb-4">
                  {isSampleListing ? 'Interested in a home like this?' : 'Ready to move forward?'}
                </p>
                <p className="text-[#1a1a1a]/70 mb-8 max-w-2xl mx-auto">
                  {isSampleListing
                    ? 'This sample listing helps you understand the kind of verified homes LiteRent is bringing to market. Join the waitlist to get notified first.'
                    : 'This property is open for LiteRent applications. Apply now to begin your review, verification, upfront payment, and direct debit setup process.'}
                </p>
                {isSampleListing || !isApplicationReady ? (
                  <button onClick={handleJoinWaitlist} className="bg-[#a5c250] text-white py-6 px-16 rounded-xl font-medium text-xl hover:bg-[#8bb83a] transition shadow-lg w-full sm:w-auto">
                    Join Waitlist
                  </button>
                ) : (
                  <button onClick={handleApplyNow} className="bg-[#294c79] text-white py-6 px-16 rounded-xl font-medium text-xl hover:bg-[#1f3a5c] transition shadow-lg w-full sm:w-auto">
                    Apply for This Home
                  </button>
                )}
              </div>
            )}

            {user && !isAdminLoggedIn && (
              <button onClick={handleSaveToggle} className={`w-full mt-4 border-2 py-6 rounded-xl font-medium text-xl transition ${isSaved ? 'border-red-500 text-red-500 hover:bg-red-50' : 'border-[#294c79] text-[#294c79] hover:bg-[#294c79]/10'}`}>
                {isSaved ? '❤️ Saved' : '♡ Save to Favorites'}
              </button>
            )}
          </div>
        </div>

        {!isAdminLoggedIn && (
          <div className="mt-16 grid md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-2xl shadow-md border border-[#e8f2d1]">
              <h3 className="text-2xl font-semibold text-[#294c79] mb-6">Property Features</h3>
              <ul className="space-y-3 text-lg">
                {['Verified and inspected property', 'Flexible payment plan available', '24/7 LiteRent support', 'Secure lease agreement'].map(feat => (
                  <li key={feat} className="flex items-center gap-3">
                    <span className="text-[#a5c250] text-2xl">✓</span>
                    <span>{feat}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-md border border-[#e8f2d1]">
              <h3 className="text-2xl font-semibold text-[#294c79] mb-6">Need Help?</h3>
              <p className="text-lg text-[#1a1a1a]/80 mb-6">Have questions about this property or the LiteRent process? Our team is here to help!</p>
              <div className="space-y-4">
                <a href="https://wa.me/2349057582412" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-[#25D366] hover:text-[#20b858] transition">
                  <span className="text-2xl">💬</span>
                  <span className="text-lg font-medium">Chat on WhatsApp</span>
                </a>
                <a href="mailto:support@literent.com.ng" className="flex items-center gap-3 text-[#294c79] hover:text-[#1f3a5c] transition">
                  <span className="text-2xl">📧</span>
                  <span className="text-lg font-medium">Email Support</span>
                </a>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
    </>
  );
}